import { useCallback, useEffect, useMemo, useState } from "react";
import { ncrAPI } from "../services/api";
import { useProjectContext } from "./useProjectContext";

export const useNCRs = () => {
  const { activeProject } = useProjectContext();
  const projectId = activeProject?.id;

  const [ncrs, setNCRs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await ncrAPI.listByProject(projectId);
      setNCRs(res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const create = useCallback(
    async (payload) => {
      if (!projectId) throw new Error("No active project selected");
      setCreating(true);
      setError(null);
      try {
        const res = await ncrAPI.create(projectId, payload);
        setNCRs((prev) => [res.data, ...prev]);
        return res.data;
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setCreating(false);
      }
    },
    [projectId]
  );

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const total = ncrs.length;
    const closed = ncrs.filter(
      (n) => (n.status || "").toLowerCase() === "closed"
    ).length;
    const open = total - closed;
    return { total, open, closed };
  }, [ncrs]);

  const upsertLocal = useCallback((next) => {
    if (!next || !next.id) return;

    setNCRs((prev) => {
      const idx = prev.findIndex((x) => x.id === next.id);
      if (idx === -1) return [next, ...prev];

      const copy = [...prev];
      copy[idx] = { ...copy[idx], ...next };
      return copy;
    });
  }, []);

  return {
    ncrs,
    loading,
    creating,
    error,
    reload: load,
    create,
    stats,
    upsertLocal,
  };
};
